import React, { useMemo } from "react";
import { Tag } from "antd";
import InfoTooltip from "@/components/UI/InfoTooltip";
import { headerColors, icons, getMessage } from "./connectionConstants";

/** A labelled fact rendered inside a connection card's info tooltip. */
export type ConnectionFact = { label: string; value: string };

/**
 * How raw data reaches Vivo. "File Export" covers vendor-delivered bundles
 * (e.g. the Florence eBinders relational CSV export) as opposed to a
 * connection Vivo pulls from itself.
 */
export type RawDataAccessType =
  | "API"
  | "Cloud Storage"
  | "FTP Server"
  | "File Export";

export type CardProps = {
  /** ISO timestamp of the last successful ingest, or a bare date for a
   * day-precision source. See formatConnectionTimestamp. */
  date_uploaded: string | null;
  data_lock_date: string | null;
  /** Named for the column: vivodb carries the fetch failure as a DateTime,
   * because fetches can run more than once a day. */
  raw_data_fetch_failure_datetime: string | null;
  data_uploaded: string | null;
  datasource_type: string;
  company_source: string;
  raw_data_access_type: RawDataAccessType;
  raw_data_cadence: string;
  /**
   * Optional inventory-sourced summary of what this feed carries, shown via an
   * info icon in the card header. Only the static per-tenant config supplies
   * it (e.g. Clario, from the raw-data inventory); backend-seeded cards omit it.
   */
  description?: string;
  /** Optional labelled facts (Format, Scope, Loaded to…) shown under the summary. */
  facts?: ConnectionFact[];
};

type ConnectionCardProps = {
  card: CardProps;
};

type ConnectionPillProps = {
  type: RawDataAccessType;
};

export type CardStatus = "default" | "error" | "locked";

const getCardStatus = (card: CardProps): CardStatus => {
  const hasLockDate = card.data_lock_date?.trim();
  const hasFailureDate = card.raw_data_fetch_failure_datetime?.trim();

  if (hasLockDate) return "locked";
  if (hasFailureDate) return "error";
  return "default";
};

export default function ConnectionCard({ card }: ConnectionCardProps) {
  const currentConfig = useMemo(() => {
    const status = getCardStatus(card);

    return {
      headerColor: headerColors[status],
      icon: icons[status],
      message: getMessage(status, card),
    };
  }, [card]);

  return (
    <div
      className="bg-white rounded-md relative shadow-omni"
      style={{ borderColor: currentConfig.headerColor }}
    >
      {/* Header */}
      <div
        className="p-3 rounded-t-md flex items-start justify-between gap-2"
        style={{ backgroundColor: currentConfig.headerColor }}
      >
        <p className="font-medium text-sm m-0">
          {card.datasource_type || "Unknown Data Source"}
        </p>
        {Boolean(card.description || card.facts?.length) && (
          <span className="shrink-0 mt-[2px] leading-none">
            <InfoTooltip
              title={
                <ConnectionTooltip
                  description={card.description}
                  facts={card.facts}
                />
              }
              fill="#556C75"
            />
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Data Source */}
        <div className="mb-4">
          <p className="text-[#556C75] text-xs m-0 mb-1">DATA SOURCE</p>
          <p className="text-sm m-0">
            {card.company_source || "Unknown Vendor"}
          </p>
        </div>

        {/* Connection and Cadence */}
        <div className="flex flex-wrap justify-between gap-x-4 gap-y-2 mb-3">
          <div className="min-w-0">
            <p className="text-[#556C75] text-xs mb-1 m-0">CONNECTION</p>
            <ConnectionPill type={card.raw_data_access_type} />
          </div>
          <div className="flex justify-end">
            <div className="text-right">
              <p className="text-[#556C75] text-xs mb-1 m-0">CADENCE</p>
              <p className="text-sm m-0">
                {card.raw_data_cadence || "Unknown"}
              </p>
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="flex gap-1 border-t pt-2">
          <div className="flex items-center gap-2">
            {currentConfig.icon}
            <div className="text-sm text-[#2B4752]">
              {currentConfig.message}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const ConnectionTooltip = ({
  description,
  facts,
}: {
  description?: string;
  facts?: ConnectionFact[];
}) => (
  <div className="flex flex-col gap-2 max-w-[280px]">
    {description ? <span className="text-xs">{description}</span> : null}
    {facts?.length ? (
      <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 m-0 text-xs">
        {facts.map((fact) => (
          <React.Fragment key={fact.label}>
            <dt className="opacity-70 whitespace-nowrap">{fact.label}</dt>
            <dd className="m-0">{fact.value}</dd>
          </React.Fragment>
        ))}
      </dl>
    ) : null}
  </div>
);

const pillConfig = {
  API: { color: "#E1F0FE", textColor: "#0066CC" },
  "Cloud Storage": { color: "#BDE0CC", textColor: "#003832" },
  "FTP Server": { color: "#0000000f", textColor: "#2B4752" },
  "File Export": { color: "#0000000f", textColor: "#2B4752" },
  Unknown: { color: "#F5F5F5", textColor: "#A0A0A0" },
};

const ConnectionPill = ({ type }: ConnectionPillProps) => {
  const config = pillConfig[type] || pillConfig["Unknown"];

  return (
    <Tag
      color={config.color}
      variant="solid"
      aria-label={`Connection type: ${type}`}
    >
      <span className="text-sm" style={{ color: config.textColor }}>
        {type || "Unknown"}
      </span>
    </Tag>
  );
};
